import {useState, type ReactNode} from 'react';
import styled from 'styled-components';
import {CodeInPlaceLink} from '../CodeInPlaceLink';

const CARDINAL = '#8c1515';

/** One question-and-answer pair in the splash FAQ. */
interface FaqEntry {
  /** The question, phrased the way a real visitor would ask it. */
  question: string;
  /** The honest (and hopefully charming) answer. */
  answer: ReactNode;
}

/**
 * The FAQ content. The voice matches the rest of the splash — warm,
 * first-person, and a little playful — and every answer is true.
 */
const FAQ_ENTRIES: FaqEntry[] = [
  {
    question: 'Why are you doing this?',
    answer: (
      <>
        Some people like to get paid. Some people like free time. We like to
        teach free courses 🙂. We have been doing it since <CodeInPlaceLink />{' '}
        began in 2020, and it is still the best part of our year.
      </>
    ),
  },
  {
    question: 'Okay, but what’s the catch?',
    answer:
      'There isn’t one. No fees, no ads, no premium tier. The closest ' +
      'thing to a catch is that we will expect you to actually do the ' +
      'work — a few focused hours a week, most weeks.',
  },
  {
    question: 'Who pays for this?',
    answer:
      'We have a really kind set of alumni who have donated money so that we can keep this free. They are really cool people.',
  },
  {
    question: 'How do you pronounce PAI?',
    answer:
      'Like "pie", as in π. Yes, we named a probability course after π. ' +
      'No, we are not even a little bit sorry.',
  },
  {
    question: 'I’m not a math person. Is this for me?',
    answer:
      'This is a class for the general public. ' +
      'In our experience, "not a math person" usually means "had one bad ' +
      'math class a while ago." If you can make sense of y = 2x + 1, you ' +
      'have everything you need — teaching you the rest is the whole point ' +
      'of the course.',
  },
  {
    question: 'Do I need to code? Do I need calculus?',
    answer:
      'No, and here is why: in 2026, many professionals build with ' +
      'probability every day without writing the code or the derivatives ' +
      'themselves. The skill that still matters is the reasoning, so that ' +
      'is what we teach — you will learn to solve problems the way the ' +
      'pros do, with the underlying math front and center.',
  },
  {
    question: 'Who are the teachers?',
    answer:
      'Volunteer section leaders from around the world — roughly one for ' +
      'every ten students. Many of them took a course like this once, ' +
      'loved it, and came back to pass it on. If that sounds like you, ' +
      'we would love to have you teach.',
  },
  {
    question: 'What do I walk away with?',
    answer:
      'Good times had with new friends. And! A public portfolio ' +
      'of everything you built, hosted by Stanford, that stays yours.',
  },
  {
    question: 'I am concerned about AI!',
    answer:
      'Right?! AI (and datacenters etc) could have really wild impacts on society, and not all of them will be good. Understanding how AI works seems especially critical in these fast moving times.',
  },
];

const List = styled.div`
  max-width: 620px;
`;

const Item = styled.div`
  border-bottom: 1px solid #efe7e7;

  &:first-child {
    border-top: 1px solid #efe7e7;
  }
`;

const QuestionButton = styled.button<{$isOpen: boolean}>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 0.25rem 0.9rem 1rem;
  background: none;
  border: none;
  border-left: 2px solid ${({$isOpen}) => ($isOpen ? CARDINAL : 'transparent')};
  text-align: left;
  font-family: inherit;
  font-size: 1rem;
  font-weight: ${({$isOpen}) => ($isOpen ? 600 : 400)};
  color: ${({$isOpen}) => ($isOpen ? CARDINAL : '#2b2b2b')};
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s,
    padding-left 0.12s;

  &:hover {
    color: ${CARDINAL};
    border-left-color: ${CARDINAL};
    padding-left: 1.25rem;
  }

  /* Keyboard focus gets the same affordance the pointer hover gives, plus an
     explicit ring so the focused row is unmistakable. */
  &:focus-visible {
    color: ${CARDINAL};
    border-left-color: ${CARDINAL};
    outline: 2px solid ${CARDINAL};
    outline-offset: 2px;
  }
`;

/**
 * The plus sign at the end of each question row. Rotates 45 degrees into a
 * small × when its entry is open — the whole "icon system" is one character.
 */
const ToggleMark = styled.span<{$isOpen: boolean}>`
  flex-shrink: 0;
  font-size: 1.35rem;
  font-weight: 300;
  line-height: 1;
  color: ${({$isOpen}) => ($isOpen ? CARDINAL : '#9a8f8f')};
  transform: rotate(${({$isOpen}) => ($isOpen ? '45deg' : '0deg')});
  transition:
    transform 0.2s ease,
    color 0.15s;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/*
 * The open/close animation uses the CSS grid 0fr-to-1fr trick: the outer
 * element transitions grid-template-rows, and the inner element clips the
 * overflow. This animates to the answer's natural height with no JS
 * measurement.
 */
const AnswerReveal = styled.div<{$isOpen: boolean}>`
  display: grid;
  grid-template-rows: ${({$isOpen}) => ($isOpen ? '1fr' : '0fr')};
  transition: grid-template-rows 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const AnswerClip = styled.div`
  overflow: hidden;
`;

const AnswerText = styled.p<{$isOpen: boolean}>`
  margin: 0;
  /* Left padding lines the answer up with the question text (which sits
     behind a 2px accent border plus its own 1rem padding). */
  padding: 0.1rem 2.5rem 1.1rem calc(1rem + 2px);
  line-height: 1.55;
  color: #4a4444;
  opacity: ${({$isOpen}) => ($isOpen ? 1 : 0)};
  /* Fade trails the height animation slightly on open, so the text arrives
     into settled space rather than mid-slide. */
  transition: opacity 0.25s ease ${({$isOpen}) => ($isOpen ? '0.12s' : '0s')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * A charming, honest FAQ for the PAI splash page: a single-open accordion in
 * the splash's cardinal-accent visual language (mirroring `PrereqCheck`'s
 * hover treatment). Opening one entry closes the previous, and answers reveal
 * with a smooth grid-row animation and a trailing text fade.
 *
 * @return The FAQ accordion.
 * @throws Never.
 */
export function SplashFaq() {
  // Index into FAQ_ENTRIES of the open entry, or null when all are closed.
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  function toggle(index: number) {
    setOpenIndex(previous => (previous === index ? null : index));
  }

  return (
    <List>
      {FAQ_ENTRIES.map((entry, index) => {
        const isOpen = openIndex === index;
        const panelId = `pai-faq-answer-${index}`;
        const questionId = `pai-faq-question-${index}`;
        return (
          <Item key={entry.question}>
            <QuestionButton
              type="button"
              id={questionId}
              $isOpen={isOpen}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => {
                toggle(index);
              }}
            >
              {entry.question}
              <ToggleMark $isOpen={isOpen} aria-hidden>
                +
              </ToggleMark>
            </QuestionButton>
            <AnswerReveal
              $isOpen={isOpen}
              id={panelId}
              role="region"
              aria-labelledby={questionId}
              aria-hidden={!isOpen}
            >
              <AnswerClip>
                <AnswerText $isOpen={isOpen}>{entry.answer}</AnswerText>
              </AnswerClip>
            </AnswerReveal>
          </Item>
        );
      })}
    </List>
  );
}
